import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../services/api'
import { colors } from '../../components/ui/styles'

interface EhrConfigRow {
  readonly id: string
  readonly ehrId: string
  readonly ehrName: string
  readonly version: string
  readonly enabled: boolean
  readonly updatedAt: string
}

const cardStyle = {
  padding: '1.25rem',
  background: colors.bgCard,
  borderRadius: '8px',
  border: `1px solid ${colors.border}`,
}

const btnStyle = {
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: 500 as const,
}

export function EhrListPage() {
  const navigate = useNavigate()
  const [configs, setConfigs] = useState<readonly EhrConfigRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true)
      const result = await api.get<readonly EhrConfigRow[]>('/ehr-configs')
      setConfigs(result.data ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load EHR configs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConfigs()
  }, [fetchConfigs])

  const handleToggle = async (id: string, enabled: boolean) => {
    try {
      await api.put(`/ehr-configs/${id}`, { enabled: !enabled })
      await fetchConfigs()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle config')
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', color: colors.textMuted }}>Loading EHR configurations...</div>
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>EHR Configurations</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => navigate('/admin/ehr-integrations/new')}
            style={{ ...btnStyle, background: '#f1f5f9', color: '#374151' }}
          >
            New Integration (Wizard)
          </button>
          <button
            onClick={() => navigate('/settings/ehr/new')}
            style={{ ...btnStyle, background: colors.primary, color: '#fff' }}
          >
            Add EHR Config
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: '0.75rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {configs.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center' as const, color: colors.textMuted }}>
          No EHR configurations found. Click "Add EHR Config" to create one.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' }}>
          {configs.map((config) => (
            <div
              key={config.id}
              style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{config.ehrName}</div>
                <div style={{ color: colors.textMuted, fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {config.ehrId} &middot; v{config.version} &middot; Updated{' '}
                  {new Date(config.updatedAt).toLocaleDateString()}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span
                  style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    background: config.enabled ? '#dcfce7' : '#f1f5f9',
                    color: config.enabled ? '#16a34a' : colors.textMuted,
                  }}
                >
                  {config.enabled ? 'Enabled' : 'Disabled'}
                </span>
                <button
                  onClick={() => handleToggle(config.id, config.enabled)}
                  style={{ ...btnStyle, background: '#f1f5f9', color: '#374151' }}
                >
                  {config.enabled ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => navigate(`/settings/ehr/${config.id}`)}
                  style={{ ...btnStyle, background: colors.primary, color: '#fff' }}
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
